import * as Dialog from '@radix-ui/react-dialog';
import { useState, useMemo, useRef, useEffect } from 'react';
import { FiX, FiSearch, FiChevronDown, FiMaximize, FiMinimize } from 'react-icons/fi';
import BorderGradient from 'components/shared/BorderGradient/BorderGradient';
import React from 'react';
import "./tiptap.css";
import * as Tooltip from '@radix-ui/react-tooltip';

interface Guide {
  id: string;
  categoryId: string;
  name: string;
  desc: string;
  link: string;
}

interface Category {
  id: string;
  name: string;
  guides?: Guide[];
}

interface GuidesModalProps {
  isOpen: boolean;
  activeTab: string;
  onClose: () => void;
}

const isGoogleDriveUrl = (url: string): boolean => {
  return url.includes('drive.google.com');
};

const getGoogleDriveEmbedUrl = (url: string): string => {
  const fileId = url.match(/\/d\/(.+?)\/view/)?.[1];
  if (fileId) {
    return `https://drive.google.com/file/d/${fileId}/preview?fs=1`;
  }
  return url;
};

export default function GuidesModal({ isOpen, activeTab, onClose }: GuidesModalProps) {
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const regularVideoRef = useRef<HTMLIFrameElement>(null);
  const maximizedVideoRef = useRef<HTMLIFrameElement>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch categories and guides when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        setLoading(true);
        try {
          // const response = await getGuideCategories();
          const response = [
            {
              "id": "a4c63bdb-d85a-4191-9168-1f1ad20f0878",
              "name": "Getting Started",
              "guides": [
                {
                  "id": "07ea489a-b4c1-440d-b967-a64608a8a5b9",
                  "categoryId": "a4c63bdb-d85a-4191-9168-1f1ad20f0878",
                  "name": "Creating an Organization and a Project",
                  "desc": "<p>This tutorial covers the process of creating an organization and a project in Process Hub.</p><p>Start by adding a new organization, entering its name and description, and modifying these details if needed. Once created, open the organization and proceed to create a project.</p><p>After creating a project, explore its key pages:</p><p><strong>Dashboard</strong> - Customize and pin important data for quick access.</p><p><strong>Tags Page</strong> - Manage and monitor tags, expressions, assets, and related documents.</p><p><strong>Alarms Page</strong> - Track alerts for potential issues with tags.</p><p><strong>Tickets Page</strong> - View reported issues, their severity, priority, and status.</p><p><strong>Data Source Integration</strong> - Connect and retrieve data from sources like PI Server, Data Center Export, Excel, Oracle, and Aviva.</p><p><strong>Users Page</strong> - Manage user access and permissions within the project.</p><p>&nbsp;</p><hr><h2>Creating an Organization and a Project - Video Script</h2><p>&nbsp;</p><p><strong>Creating an Organization and a Project - Step by Step</strong></p><p>&nbsp;</p><p>Welcome. In this video, you'll learn how to create an organization and your first project.</p><p>&nbsp;</p><h3><strong>Creating an Organization</strong></h3><p>&nbsp;</p><p>On the main screen, you'll see a list of demo organizations</p><p>Click on the \"Add New\" button to create a new organization</p><p>Enter the Organization Name and a brief Organization Description</p><p>Click the \"Create Organization\" button to confirm</p><p>Once created, click on your organization in the list to open it</p><p>If you need to edit the Organization Name or Description, click the edit icon next to the name and make the necessary changes</p><h3><strong>Creating a Project</strong></h3><p>&nbsp;</p><p>Inside your organization, click on the \"Add New\" button to create a project</p><p>Enter the Project Name</p><p>Provide a short description explaining what you are going to build</p><p>Select a Product Location from the list</p><p>Choose the location on the map where you want the project to be displayed</p><p>Click \"Create Project\" to confirm</p><p>Wait for the project to appear in the list</p><p>From here, you can click on your project to open and start working on it</p><p>If you need to update the Project Name, Description, or Location, click the edit icon next to the name and modify the fields accordingly</p><p>&nbsp;</p><p>Now you know how to create an organization and start a new project.</p>",
                  "link": "drive.google.com/file/u/1/d/1jaZXVYkU28hwB7YSTof-OJ8jTrGhxN_H/view?usp=drive_link"
                },
                {
                  "id": "a3b6ad89-4f9a-4039-9f13-d29ef28239e5",
                  "categoryId": "a4c63bdb-d85a-4191-9168-1f1ad20f0878",
                  "name": "Controls Overview",
                  "desc": "<p>Learn the basic controls of ProcessHub and how to navigate the environment efficiently.</p><p>In this video, we cover the following essential controls:<br>To move, use the <strong>WASD </strong>keys or the <strong>arrow keys</strong>. Hold <strong>Shift</strong> to sprint. To cycle between camera views, press <strong>F</strong> or click the icon near the minimap. In drone view, use the same movement keys, <strong>E</strong> to ascend, and <strong>Q</strong> to descend.<br>For the navigation camera, use the same movement keys, hold the <strong>middle mouse button</strong> to reposition the view, and <strong>hold the right mouse button</strong> to rotate around the central marker.<br>To interact with objects, <strong>left-click</strong> for primary actions, such as opening widgets, and <strong>right-click</strong> for secondary actions, such as opening widget configuration windows.</p><p><br></p>",
                  "link": "https://drive.google.com/file/d/1XP08RMsAlFL6QUfzOyAcJPAbhkHE7lR_/view?usp=drive_link"
                },
                {
                  "id": "95043d7d-0ee5-4e47-943a-b0adb1e73ec2",
                  "categoryId": "a4c63bdb-d85a-4191-9168-1f1ad20f0878",
                  "name": "Notes Overview",
                  "desc": "<p>Learn how to create and manage notes in Process Hub.</p><p>To create a note, press <strong>Escape</strong> to enable the cursor. <strong>Click the note icon</strong> in the top-left menu or press the <strong>N</strong> key on your keyboard. Enter a title and write your note in the designated field.<br>If needed, attach <strong>.PDF, .XLSX, or .XLS</strong> files using the file attachment option. When you're done, click the <strong>Create button</strong> at the bottom of the pop-up window.</p><p>Your note will be saved to the server, making it accessible to other users. Attached files can also be viewed and downloaded by others.</p>",
                  "link": "https://drive.google.com/file/d/153Lfoecqf76lJ6G6FWhX_dDTbhU9g7ec/view?usp=drive_link"
                },
                {
                  "id": "9b01d658-f777-4bcf-b910-162050f14501",
                  "categoryId": "a4c63bdb-d85a-4191-9168-1f1ad20f0878",
                  "name": "Widgets Overview",
                  "desc": "<p>This tutorial covers creating and managing widgets in Process Hub.</p><p>Enter Editing Mode by<strong> clicking the gear icon</strong> in the top-left menu, then open the Widget Tab. <strong>Click the Plus icon </strong>to create a widget, adjust its position with gizmos, and enter a name, description, and unit. If creating a child widget, select the equipment and assign tags.</p><p>To display tag information, click the<strong> Plus icon in the Actions column</strong>. Arrange tags, add text items, and enable constant widget visibility if needed.</p><p>Interact with widgets by <strong>left-clicking</strong> to open them. View assigned tags, pin values, set alarms, create tickets, and analyze data with graphs. Documents can be attached in the Documents tab.</p>",
                  "link": "https://drive.google.com/file/d/1wLxwD_YcVrnRxihCT8gOd7mxn35QNBW5/view?usp=drive_link"
                },
                {
                  "id": "817339e2-6ba4-4607-ba59-4d540febc777",
                  "categoryId": "a4c63bdb-d85a-4191-9168-1f1ad20f0878",
                  "name": "Editing Mode Overview",
                  "desc": "<p>This tutorial covers the editing mode in Process Hub, where you can build and manage scenes. <br>Enter Editing Mode by<strong> clicking the gear icon</strong> in the top-left menu and access the Object Library.</p><p>Objects snap to a grid by default, with adjustable settings for visibility and snapping.</p><p>To place objects, select one from the library, position it, and click to place. Use platforms for grouping or the resizable hangar for a dedicated space. Rotate objects with <strong>Q</strong> and <strong>E</strong> or drag corners after placement.</p><p><strong>Click the pipe icon </strong>to enter connecting mode. Connect objects with pipes by selecting nodes and adjust them later if needed. Copy objects using <strong>Ctrl + C</strong> and paste with <strong>Ctrl + V</strong>. Use <strong>Shift</strong> to select multiple objects.</p><p>Manage objects via the Object Hierarchy and object’s Pop-Up Window, where you can rename, reposition, and focus the camera. Adjust widgets, notes, and labels using gizmos for precise placement.</p><p><br></p>",
                  "link": "https://drive.google.com/file/d/1JKNdEV-1AVsxxS3Ic1WURYaT1V_tgIFh/view?usp=drive_link"
                }
              ]
            }
          ];
          if (response) {
            const data = response;
            setCategories(data);
            // Expand first category by default
            if (data.length > 0) {
              const firstCategory = data[0];
              setExpandedMenus([firstCategory.id]);
              // Select first guide if available
              if (firstCategory.guides && firstCategory.guides.length > 0) {
                setSelectedGuide(firstCategory.guides[0]);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching guides:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [isOpen]);

  // Filter categories and guides based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories;

    const searchLower = searchTerm.toLowerCase();

    return categories.map(category => {
      const matchingGuides = category.guides?.filter(guide =>
        guide.name.toLowerCase().includes(searchLower) ||
        guide.desc.toLowerCase().includes(searchLower)
      );

      if (
        category.name.toLowerCase().includes(searchLower) ||
        (matchingGuides && matchingGuides.length > 0)
      ) {
        return {
          ...category,
          guides: matchingGuides
        };
      }
      return null;
    }).filter(Boolean) as Category[];
  }, [searchTerm, categories]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const handleGuideSelect = (guide: Guide) => {
    setSelectedGuide(guide);
    // Reset scroll position when changing guides
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  const VideoPlayer = ({ url, title }: { url: string, title: string }) => {
    const embedUrl = isGoogleDriveUrl(url) ? getGoogleDriveEmbedUrl(url) : url;

    const toggleFullscreen = () => {
      setIsMaximized(!isMaximized);
    };

    return (
      <div className="relative aspect-video">
        {/* Title overlay */}
        <div className="pb-4 flex justify-center">
          <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
        </div>

        <iframe
          ref={regularVideoRef}
          src={`${embedUrl}${embedUrl.includes('?') ? '&' : '?'}enablejsapi=1`}
          className="w-full h-full rounded-lg"
          frameBorder="0"
          allow="autoplay; encrypted-media;"
          sandbox="allow-same-origin allow-scripts"
        />

        {/* Maximize Button */}
        <button
          onClick={toggleFullscreen}
          className="absolute bottom-2 right-4 p-2 bg-black rounded-lg text-white transition-colors"
        >
          <FiMaximize size={20} />
        </button>
      </div>
    );
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content
          className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 transition-all duration-300 ${isMaximized
              ? 'w-screen h-screen max-w-none !translate-x-0 !translate-y-0 !left-0 !top-0'
              : 'w-[90vw] max-w-6xl'
            }`}
        >
          <BorderGradient noBorder={isMaximized} className={`rounded-lg ${isMaximized ? 'h-screen' : ''}`}>
        <div className={`bg-[#1A1A1A] rounded-lg flex flex-col ${isMaximized ? 'h-screen' : 'h-[80vh]'}`}>
              {/* Content */}
              <div className="flex flex-1 overflow-hidden">
                {/* Left Sidebar */}
                <div className={`${isMaximized ? 'hidden' : 'w-64'} bg-[#353535] rounded-lg border-r border-white/[0.08] overflow-y-auto`}>
                  {/* Sidebar Header */}
                  <div className="p-4 border-b border-white/[0.08]">
                    <h2 className="text-lg font-semibold text-white mb-3">Guides & Tutorials</h2>
                    <div className="relative">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          if (e.target.value) {
                            setExpandedMenus(categories.map(cat => cat.id));
                          } else {
                            setExpandedMenus(categories.length > 0 ? [categories[0].id] : []);
                          }
                        }}
                        placeholder="Search guides..."
                        className="w-full bg-[#2A2A2A] rounded-lg pl-8 pr-3 py-1.5 text-sm text-white border border-white/[0.08] focus:outline-none focus:border-white/[0.16]"
                      />
                      <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    </div>
                  </div>

                  {/* Categories and Guides */}
             {categories.length > 0 ?     <div className="py-2">
                    {loading ? (
                      <div className="p-4 text-center text-gray-400">Loading...</div>
                    ) : (
                      filteredCategories.map((category) => (
                        <div key={category.id}>
                          <button
                            onClick={() => toggleMenu(category.id)}
                            className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-[#2A2A2A] transition-colors"
                          >
                            <span className={`${expandedMenus.includes(category.id) ? 'text-white' : 'text-gray-400'}`}>
                              {category.name}
                            </span>
                            <FiChevronDown
                              className={`text-gray-400 transition-transform duration-200 ${expandedMenus.includes(category.id) ? 'rotate-180' : ''}`}
                            />
                          </button>
                          {expandedMenus.includes(category.id) && (
                            <div className="pl-4">
                              {category.guides?.map((guide) => (
                                <button
                                  key={guide.id}
                                  onClick={() => handleGuideSelect(guide)}
                                  className={`w-full px-4 py-2 text-left hover:bg-[#2A2A2A] transition-colors ${selectedGuide?.id === guide.id
                                      ? 'text-white bg-[#2A2A2A]'
                                      : 'text-gray-400'
                                    }`}
                                >
                                  <div className="max-w-[200px] truncate">
                                    <Tooltip.Provider>
                                      <Tooltip.Root>
                                        <Tooltip.Trigger asChild>
                                          <span className="truncate block">
                                            {guide.name}
                                          </span>
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                          <Tooltip.Content
                                            className="bg-[#2A2A2A] text-white px-3 py-2 rounded text-sm max-w-[300px] z-[100]"
                                            sideOffset={5}
                                          >
                                            {guide.name}
                                          </Tooltip.Content>
                                        </Tooltip.Portal>
                                      </Tooltip.Root>
                                    </Tooltip.Provider>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>: <h5 className='m-10'>No Category found!</h5>}
                </div>

                {/* Right Content */}
                {categories.length > 0 ?  
                <div className={`flex-1 ${isMaximized ? 'p-0' : 'p-6 mt-8'} overflow-y-auto ProseMirror`}>
                  {selectedGuide ? (
                    <div className={`relative ${isMaximized ? 'h-full' : ''}`}>
                      <div className="w-full flex justify-center h-full">
                        {isMaximized ? (
                          <div className="w-full h-full relative">
                            <iframe
                              ref={maximizedVideoRef}
                              src={`${getGoogleDriveEmbedUrl(selectedGuide.link)}${selectedGuide.link.includes('?') ? '&' : '?'}enablejsapi=1`}
                              className="w-full h-full"
                              frameBorder="0"
                              allow="autoplay; encrypted-media;"
                              sandbox="allow-same-origin allow-scripts"
                            />
                            <div className="absolute bottom-[-10px] right-0 p-4 flex gap-2">
                              <button
                                onClick={() => setIsMaximized(false)}
                                className="p-2 bg-black rounded-lg text-white transition-colors"
                              >
                                <FiMinimize size={20} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-[600px]">
                            <VideoPlayer
                              url={selectedGuide.link}
                              title={selectedGuide.name}
                            />
                          </div>
                        )}
                      </div>

                      {!isMaximized && (
                        <div className="mt-6">
                          <div className="text-gray-400 whitespace-pre-wrap prose prose-invert"
                            dangerouslySetInnerHTML={{ __html: selectedGuide.desc }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      Select a guide from the sidebar to view its content
                    </div>
                  )}
                </div> : <h5 className='flex items-center mx-auto'>No data found!</h5>}
              </div>

              {/* Close button - only show when not maximized */}
              {!isMaximized && (
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 text-gray-400 hover:text-white"
                >
                  <FiX size={24} />
                </button>
              )}
            </div>
          </BorderGradient>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
} 